import { useCallback } from 'react'

import { Modal, useModals } from 'almanac-ui/Modal'

import { BrandPlan, ContentStrategy, ContentStrategyType } from 'types'
import { ContentTypeCounts } from 'utils/brandPlans/contentCategories'
import { deriveInitialCountsFromStrategy } from './contentTypeDefaultsUtils'
import { useCreateCampaignMachine } from './useCreateCampaignMachine'
import { StateId, EventId, CampaignRow } from './Types'
import { StrategySelection } from './StrategySelection'
import { ContentTypeDefaults } from './ContentTypeDefaults'
import { CampaignDetails } from './CampaignDetails'
import { ContentStrategyTypeLabel } from '../../../constants'
import { EmojiMap } from 'components/ContentStrategyTypeEmoji/ContentStrategyTypeEmoji'

interface CreateCampaignModalProps {
  brandPlan: BrandPlan
  onSuccess?: () => void
}

export const CreateCampaignModal = ({ brandPlan, onSuccess }: CreateCampaignModalProps) => {
  const [current, send] = useCreateCampaignMachine({ brandPlan, onSuccess })
  const { closeModal } = useModals()

  const strategyType = current.context.strategyType
  const title =
    (current.matches(StateId.ReviewDetails) || current.matches(StateId.Creating)) && strategyType
      ? `Review and customize your campaigns for ${EmojiMap[strategyType]} ${ContentStrategyTypeLabel[strategyType]}`
      : 'Create New Campaign'

  const handleNext = useCallback(
    (strategyType: ContentStrategyType, strategyConfig: ContentStrategy, campaignCount: number) => {
      send({ type: EventId.Next, strategyType, strategyConfig, campaignCount })
    },
    [send],
  )

  const handleContentTypesConfirm = useCallback(
    (counts: ContentTypeCounts) => {
      send({ type: EventId.SetContentTypes, counts })
    },
    [send],
  )

  const handleBack = useCallback(() => {
    send({ type: EventId.Back })
  }, [send])

  const handleCancel = useCallback(() => {
    closeModal()
  }, [closeModal])

  const handleCreate = useCallback(
    (campaigns: CampaignRow[]) => {
      send({ type: EventId.Create, campaigns })
    },
    [send],
  )

  const isCreating = current.matches(StateId.Creating)
  const showDetails = current.matches(StateId.ReviewDetails) || isCreating
  const showContentTypeDefaults = current.matches(StateId.ContentTypeDefaults)
  const modalSize = showDetails || showContentTypeDefaults ? 'md' : 'sm'

  return (
    <Modal.Root size={modalSize} blaze>
      {!showContentTypeDefaults && <Modal.Header title={title} />}
      {current.matches(StateId.SelectStrategy) && (
        <StrategySelection brandPlan={brandPlan} onNext={handleNext} onCancel={handleCancel} />
      )}
      {showContentTypeDefaults && (
        <ContentTypeDefaults
          initialCounts={
            current.context.contentTypeCounts ?? deriveInitialCountsFromStrategy(current.context.strategyConfig)
          }
          onConfirm={handleContentTypesConfirm}
          onBack={handleBack}
        />
      )}
      {showDetails && (
        <CampaignDetails
          campaigns={current.context.campaigns}
          onBack={handleBack}
          onCreate={handleCreate}
          isSubmitting={isCreating}
        />
      )}
    </Modal.Root>
  )
}
